/* 陳到（字叔至）
 * v0.5.0 新武將：用「能」（docs/ENERGY-SURVEY.md §6），四招都是兩段式；技能 v1 照 LoL 李星（耗能減半、射程 ×0.8）。
 * 史實：劉備帳下白毦兵的統領，「名位常亞趙雲，俱以忠勇稱」（《季漢輔臣贊》）。 */
import { SG, gd, landing, move, spMul, dashTo } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, S, D } from '../define.ts';

const ID = 'chendao';
export default defineHero({
  id: ID, name: '陳到', sub: '字叔至', faction: 'shu', role: '刺客', weapon: 'sword', res: 'neng',
  look: {
    knife: true, weaponName: '短刀', head: 'helmet', eye: 'fierce', beard: 'short', skin: '#e2b48a', fx: '#eef3ff',
    c: { armor: '#5d6a74', sleeve: '#3e7b50', cloth: '#2f5b42', trim: '#e8e6dc', pants: '#2b3530', boot: '#3a3f44', cape: '#e9ebe6', belt: '#8a6b3a', metal: '#c9d0d6', plume: '#f4f4ee', pole: '#6b4a2e' },
  },
  passive: ['白毦兵', '放完技能後 3 秒內的 2 次普攻攻速 +40%，各回 10 能。'],
  skills: [
    ['刀敵', 'bolt', '朝方向擲出短刀，命中第一個敵人造成 {55/88/122/155} 物理傷害並現形 3 秒。3 秒內再按一次：「追鋒」。',
      { x: { k: 'cdSonic', dmg: [55, 88, 122, 155], speed: 1440, at: 0.3 }, aim: D(960, 36), shape: 'knife', cd: [10, 8.7, 7.3, 6], mp: 25, recast: 3, thenOn: 'hit', el: null,
        then: ['追鋒', 'charge', '衝向被「刀敵」命中的敵人，造成 {55/88/122/155} 物理傷害，對方每少 1% 生命傷害 +1%（最多 ×2）。', { x: { k: 'cdStrike', dmg: [55, 88, 122, 155], speed: 1350, at: 0.05 }, aim: S(0), mp: 13, el: null }] }],
    ['護駕', 'dash', '飛到友方武將或小兵身邊（不選人就是自己），兩人各得 {70/130/190/250} 護盾 2 秒；選自己時冷卻減半。3 秒內再按一次：「鐵骨」。',
      { x: { k: 'cdGuard', shield: [70, 130, 190, 250], speed: 1400, at: 0.05 }, aim: U(560), wardHop: true, cd: 12, mp: 25, recast: 3, el: null,
        then: ['鐵骨', 'buff', '4 秒內普攻吸血、技能吸血 {5/11/17/23}%。', { x: { k: 'cdIron', ls: [5, 11, 17, 23], dur: 4, at: 0.05 }, aim: S(0), mp: 13, el: null }] }],
    ['亂舞', 'roar', '刀光一閃，立即對周圍 280 的敵人造成 {35/75/115/155} 法術傷害並現形 4 秒。3 秒內再按一次：「斷筋」。',
      { x: { k: 'cdTempest', dmg: [35, 75, 115, 155], r: 280, at: 0.15 }, aim: S(280), cd: 8, mp: 25, recast: 3, thenOn: 'hit', el: null,
        then: ['斷筋', 'roar', '被「亂舞」打中、400 內的敵人緩速 {20/40/60/80}%，4 秒內遞減。', { x: { k: 'cdCripple', slow: [20, 40, 60, 80], r: 400, dur: 4, at: 0.05 }, aim: S(400), mp: 13, el: null }] }],
    ['龍尾掃', 'multi', '對一名敵將造成 {175/400/625} 物理傷害並把他掃飛 560；沿路撞到的敵人受到同樣傷害，加上被掃飛者最大生命 {6/7.5/9}%，並被擊飛 1 秒。',
      { x: { k: 'cdKick', dmg: [175, 400, 625], hpP: [6, 7.5, 9], kb: 560, at: 0.35 }, aim: U(300), cd: [110, 85, 60], mp: 0, el: null, shout: '白毦在此，誰敢犯駕！' }],
  ],
  kinds: {
    // 刀敵（天音波）：擲出短刀，命中第一個敵人；打中才開第二段，並記住他
    cdSonic(u, a, X) {
      const sp = spMul(u);
      this.shootProj(u, { dir: a.dir, speed: X.speed, range: this.skillRange(a.sk, a.lvl), rad: 30, shape: 'knife', col: u.spec.fx, big: true,
        onHit: (t: Unit) => {
          if (t.structure) return true;
          this.damage(u, t, X.dmg * sp, { stagger: 0.25, sk: true });
          this.expose(t, u.team, 3);
          this.armThen(u, 0, t);
          this.fx({ k: 'spark', x: t.x, y: t.y - 30, col: u.spec.fx });
          return true;
        } });
    },
    // 追鋒（回音擊）：衝向被標記的人，依對方已損生命加傷
    cdStrike(u, a, X) {
      const t = a.rcTgt as Unit;
      if (!t || !t.alive) return;
      const sp = spMul(u);
      dashTo(this, u, t, X.speed, (o) => {
        const miss = 1 - o.hp / o.maxHp;
        this.damage(u, o, X.dmg * sp * (1 + Math.min(1, miss)), { stagger: 0.35, sk: true });
        this.fx({ k: 'slam', x: o.x, y: o.y, r: 80, col: u.spec.fx });
      });
    },
    // 護駕（金鐘罩）：飛到友將身邊，兩人都拿護盾；選自己冷卻減半
    cdGuard(u, a, X) {
      const R = this.skillRange(a.sk, a.lvl);
      // 點到的友方武將或小兵（離點的位置 130 內最近的一個）；沒點到人照一般規則（殘血的友將、沒有就自己）
      const near = this.units.filter((o: Unit) => o.alive && o !== u && o.team === u.team && (o.kind === 'hero' || o.kind === 'minion')
        && gd(u.x, u.y, o.x, o.y) <= R + 60 && gd(a.pt[0], a.pt[1], o.x, o.y) < 130)
        .sort((p: Unit, q: Unit) => gd(a.pt[0], a.pt[1], p.x, p.y) - gd(a.pt[0], a.pt[1], q.x, q.y))[0];
      const t = near || this.xAlly(u, a.pt, R, true);
      const amt = X.shield * spMul(u);
      const shield = (h: Unit) => { h.st.shield = Math.max(h.st.shield || 0, amt); h.st.shieldT = Math.max(h.st.shieldT || 0, 2); this.fx({ k: 'heal', x: h.x, y: h.y, col: u.spec.fx }); };
      const fin = () => { if (u.act && u.act.sk === a.sk) this.endAct(u); }; // 到了就收招，不再播後面的突刺
      const ward = this.hopWard(u, a);
      if (ward) { dashTo(this, u, ward, X.speed, () => { shield(u); fin(); }); return; } // 跳眼：飛到自己的眼，只有自己得到護盾
      if (!t || t === u) { shield(u); const r = u.rc && u.rc[1]; if (r) r.cd0 *= 0.5; fin(); return; }
      dashTo(this, u, t, X.speed, (o) => { shield(u); shield(o); fin(); });
    },
    // 鐵骨（鐵布衫）：普攻吸血＋技能吸血
    cdIron(u, a, X) {
      this.addBuff(u, { id: a.sk.name, t: X.dur, ls: X.ls / 100 });
      u.cdVamp = { until: this.t + X.dur, p: X.ls / 100 };
      this.fx({ k: 'heal', x: u.x, y: u.y, col: '#ffd46b' });
    },
    // 亂舞（天雷破）：照震地，立即打周圍：周圍法術傷害、現形；打中才開第二段
    cdTempest(u, a, X) {
      const sp = spMul(u), hit: number[] = [];
      this.enemiesNear(u, u.x, u.y, X.r).forEach((t) => {
        if (t.structure) return;
        this.damage(u, t, X.dmg * sp, { magic: true, spell: true, stagger: 0.25 });
        this.expose(t, u.team, 4);
        hit.push(t.id);
      });
      this.fx({ k: 'slam', x: u.x, y: u.y, r: X.r, col: u.spec.fx });
      if (hit.length) { u.cdHit = hit; this.armThen(u, 2); }
    },
    // 斷筋（摧筋斷骨）：被亂舞打中的人緩速，4 秒內遞減
    cdCripple(u, a, X) {
      const ids = new Set(u.cdHit || []); u.cdHit = null;
      const t0 = this.t;
      const ts = this.units.filter((t) => ids.has(t.id) && t.alive && gd(u.x, u.y, t.x, t.y) <= X.r);
      const tick = () => {
        const k = 1 - (this.t - t0) / X.dur;
        if (k <= 0) return;
        ts.forEach((t) => { if (t.alive) this.addBuff(t, { id: '斷筋', t: 0.3, neg: true, ms: -(X.slow / 100) * k }); });
        this.later(0.25, tick);
      };
      tick();
      ts.forEach((t) => this.fx({ k: 'spark', x: t.x, y: t.y - 20, col: '#b8c4ff' }));
    },
    // 龍尾掃（猛龍擺尾）：把敵將掃飛，沿路撞到的敵人受傷並被擊飛
    cdKick(u, a, X) {
      const t = this.xPick(u, a.pt, 150);
      if (!t || t.kind !== 'hero') { this.xRefund(u, a); return; }
      const dmg = X.dmg * spMul(u);
      this.damage(u, t, dmg, { stagger: 0.5, sk: true });
      this.fx({ k: 'slam', x: t.x, y: t.y, r: 90, col: u.spec.fx });
      if (!t.alive) return;
      this.applyCC(u, t, { stun: 0.45 }); // 飛出去這段時間不能動
      const dir = SG.dirTo(u, t.x, t.y), n = 14, hit = new Set([t.id]), bonus = t.maxHp * X.hpP / 100;
      for (let j = 1; j <= n; j++) this.later(0.03 * j, () => {
        if (!t.alive) return;
        move(t, (dir[0] * X.kb) / n, (dir[1] * X.kb) / n);
        this.enemiesNear(u, t.x, t.y, t.r + 40).forEach((o) => {
          if (hit.has(o.id) || o.structure) return;
          hit.add(o.id);
          this.damage(u, o, dmg + bonus, { stagger: 0.4, sk: true });
          if (o.alive) this.applyCC(u, o, { knockup: 1 });
          this.fx({ k: 'spark', x: o.x, y: o.y - 30, col: u.spec.fx });
        });
      });
    },
  },
  hooks: [
    // 白毦兵（連擊）：放完技能後 3 秒內的 2 次普攻攻速 +40%、各回 10 能
    hook('cast.after', 20, function (u) {
      if (u.spec.id !== ID) return;
      u.cdFlurry = { n: 2, until: this.t + 3 };
      this.addBuff(u, { id: '白毦兵', t: 3, as: 40 });
    }),
    hook('onHit', 20, function (u, t) {
      const f = u.cdFlurry;
      if (!f || u.spec.id !== ID) return;
      if (f.until < this.t) { u.cdFlurry = null; return; }
      u.mp = Math.min(u.maxMp, u.mp + 10);
      if (--f.n <= 0) { u.cdFlurry = null; this.addBuff(u, { id: '白毦兵', t: 0.01 }); }
    }),
    // 鐵骨：技能傷害也吸血
    hook('damage.after', 20, function (c, lost) {
      const v = c.own.cdVamp;
      if (!v || lost <= 0 || c.t.structure || !(c.o && c.o.sk)) return;
      if (v.until < this.t) { c.own.cdVamp = null; return; }
      this.heal(c.own, lost * v.p);
    }),
  ],
  // 電腦：Q 打得到就丟、打中就追；W 隊友殘血就飛過去，自己掉血就開鐵骨；E 貼身就放、打中就緩速；R 對方殘血或能掃到兩人以上
  ai: {
    cdSonic: ({ t, d, hero, u }) => !!t && d < 900 && (hero || u.mp > 60),
    cdStrike: ({ w, u, hpR }) => { const r = u.rc && u.rc[0], t = r && r.tgt; return !!t && t.alive && (t.kind !== 'hero' || t.hp / t.maxHp < 0.6 || hpR > 0.5) && w.enemiesNear(u, t.x, t.y, 500).filter((e) => e.kind === 'tower' && e.alive).length === 0; },
    cdGuard: ({ w, u, t, hero, hpR }) => (hero && hpR < 0.5) || w.units.some((a) => a.alive && a !== u && a.team === u.team && a.kind === 'hero' && a.hp / a.maxHp < 0.4 && gd(u.x, u.y, a.x, a.y) < 560 && gd(u.x, u.y, a.x, a.y) > 150),
    cdIron: ({ hpR, hero }) => hero && hpR < 0.75,
    cdTempest: ({ t, d, hero, u }) => !!t && d < 250 && (hero || u.mp > 50),
    cdCripple: ({ hero }) => hero,
    cdKick: ({ w, t, d, hero }) => hero && d < 320 && (t!.hp / t!.maxHp < 0.45 || w.units.some((o) => o.alive && o.kind === 'hero' && o.team === t!.team && o !== t && gd(o.x, o.y, t!.x, t!.y) < 450)),
  },
});
